import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Target } from 'lucide-react';
import Image1 from "../../assets/bfsi.webp";
import Image2 from "../../assets/bfsi2.webp";

const Bfsi = () => {
    const [openIndex, setOpenIndex] = useState(0);
    const navigate = useNavigate();

    const handleSignupClick = () => {
        navigate("/login?mode=signup");
    };

    const solutions = [
        {
            title: "Secure OTP & Authentication",
            description: "Protect every transaction with high-priority OTP delivery. Our encrypted channels ensure that sensitive authentication codes reach your customers in milliseconds, minimizing login friction and fraud."
        },
        {
            title: "Real-time Transaction Alerts",
            description: "Keep customers informed with instant SMS notifications for withdrawals, deposits, and card swipes. Enhance transparency and security by providing a real-time audit trail directly on their mobile devices."
        },
        {
            title: "Payment & EMI Reminders",
            description: "Reduce delinquency rates with automated payment nudges. Schedule personalized reminders for loan EMIs, credit card bills, and insurance premiums to ensure timely collections without manual intervention."
        },
        {
            title: "Fraud Prevention Alerts",
            description: "Act fast against suspicious activity. Send immediate alerts to customers when unusual patterns are detected, allowing them to verify or block transactions instantly through two-way SMS."
        },
        {
            title: "Policy & Account Updates",
            description: "Simplify banking by delivering account balance statements, policy renewal links, and interest rate updates via secure, interactive messaging solutions."
        }
    ];

    // BFSI-specific workflow tags
    const row1 = ["OTP Verification", "Fund Updates", "Account Opening", "Credit Card Activation", "Loan Approval", "Stock Alerts"];
    const row2 = ["Balance Inquiry", "Insurance Renewal", "KYC Update", "EMI Reminder", "Fixed Deposit", "Fraud Alert"];

    return (
        <div className="font-sans text-slate-900 overflow-x-hidden">
            {/* Premium Animation Styles */}
            <style>
                {`
                    @keyframes scroll-left {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    @keyframes scroll-right {
                        0% { transform: translateX(-50%); }
                        100% { transform: translateX(0); }
                    }
                    .animate-scroll-left {
                        display: flex;
                        width: max-content;
                        animation: scroll-left 40s linear infinite;
                    }
                    .animate-scroll-right {
                        display: flex;
                        width: max-content;
                        animation: scroll-right 40s linear infinite;
                    }
                    .pause-on-hover:hover {
                        animation-play-state: paused;
                    }
                `}
            </style>

            {/* --- SECTION 1: HERO SECTION --- */}
            <section className="bg-[#EEF2FC] flex flex-col md:flex-row items-center justify-between px-6 py-10 md:px-12 lg:px-24 min-h-[600px]">
                <div className="w-full md:w-1/2 mb-10 md:mb-0 md:pr-10 pt-12 md:pt-1 flex flex-col items-start -mt-16">
                    <h1 className="text-4xl md:text-5xl font-medium text-slate-900 leading-tight tracking-tight mb-6">
                        Eurofox — Fast Financial Messages, Trusted Connections
                    </h1>
                    <p className="text-lg text-slate-700 mb-8 leading-relaxed">
                        Securely deliver OTPs, transaction alerts, and payment reminders in real-time. Build trust with your customers through instant, reliable, and encrypted bulk SMS solutions designed for the financial sector.
                    </p>
                    <button
                        onClick={() => navigate('/contact')}
                        className="inline-flex items-center gap-2 px-6 py-3 text-white bg-slate-900 rounded-lg transition duration-300 shadow-md"
                    >
                        Request a Demo
                        <ChevronRight className="w-5 h-5 animate-bounce" />
                    </button>
                </div>

                <div className="w-full md:w-1/2 flex justify-center">
                    <img
                        src={Image1}
                        alt="Banking and Finance SMS Solutions"
                        className="relative object-contain -left-[10px] -top-[56px] max-w-lg 
               w-[80%] h-auto 
               md:w-auto md:h-[480px]"
                    />
                </div>
            </section>

          {/* --- SECTION 2: BFSI PROCESSES (Accordion) --- */}
<section className="bg-[#EEF2FC] py-12 lg:py-32 -mt-16 lg:-mt-32">
    <div className="container mx-auto px-4 lg:px-12">
        <div className="text-center max-w-4xl mx-auto mb-10 lg:mb-24">
            <h2 className="text-3xl md:text-4xl lg:text-6xl font-medium text-slate-900 leading-tight">
                How Eurofox can help <br className="hidden md:block" />
                <span className="text-[#F46653]">Financial operations</span>
            </h2>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            {/* Image Placeholder Left */}
            <div className="w-full lg:w-5/12">
                <div className="relative rounded-2xl overflow-hidden shadow-lg w-full h-[250px] md:h-[300px] lg:h-[530px] bg-slate-200">
                    <img
                        src={Image2}
                        alt="Eurofox BFSI Process"
                        className="w-full h-full object-cover object-center"
                    />
                </div>
            </div>

            {/* Questions/Answers Right */}
            <div className="w-full lg:w-7/12 space-y-3 lg:space-y-4">
                {solutions.map((item, index) => (
                    <div key={index} className="bg-white border border-slate-200 rounded-2xl shadow-sm transition-all duration-300">
                        <button
                            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                            className="w-full flex items-center justify-between p-4 lg:p-6 text-left hover:bg-slate-50 transition-colors rounded-2xl"
                        >
                            <span className="font-medium text-slate-800 text-base md:text-lg lg:text-xl pr-4">
                                {item.title}
                            </span>
                            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`} />
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="px-4 lg:px-6 pb-4 lg:pb-6 text-slate-600 text-sm md:text-base lg:text-lg leading-relaxed border-t border-slate-50 pt-3 lg:pt-4">
                                {item.description}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
</section>
{/* --- SECTION 3: WORKFLOW ANIMATION --- */}
<section className="bg-[#EEF2FC] py-16 lg:py-32 border-t border-slate-100">
    <div className="container mx-auto px-4 md:px-6 lg:px-12 text-center">
        
        {/* Adjusted negative margin and text sizes */}
        <h2 className="text-3xl md:text-4xl lg:text-6xl font-medium text-slate-900 mb-6 lg:mb-6 tracking-tight -mt-10 lg:-mt-24">
            Secure banking, instant alerts — <span className="text-[#F46653]">SMS solutions</span>
            <br className="hidden md:block" />
            <span className="text-[#F46653]"> for modern finance</span>
        </h2>

        {/* Scaled down top/bottom margins for mobile */}
        <div className="relative mt-10 mb-10 lg:mt-20 lg:mb-20 overflow-hidden">
            
            {/* Reduced gradient width on mobile so it doesn't cover the whole screen */}
            <div className="absolute inset-y-0 left-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-r from-[#EEF2FC] to-transparent"></div>
            <div className="absolute inset-y-0 right-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-l from-[#EEF2FC] to-transparent"></div>

            {/* Added 'flex' to ensure gap works, adjusted gaps for mobile */}
            <div className="animate-scroll-left flex gap-4 lg:gap-8 mb-4 lg:mb-8">
                {[...row1, ...row1].map((tag, idx) => (
                    <div
                        key={idx}
                        className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                    >
                        {tag}
                    </div>
                ))}
            </div>

            <div className="animate-scroll-right flex gap-4 lg:gap-8">
                {[...row2, ...row2].map((tag, idx) => (
                    <div
                        key={idx}
                        className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                    >
                        {tag}
                    </div>
                ))}
            </div>
        </div>

        {/* Scaled down text size and margins */}
        <p className="text-base md:text-lg lg:text-2xl text-slate-600 font-normal max-w-4xl mx-auto leading-relaxed mb-10 lg:mb-16 px-2 lg:px-0">
            Empower your financial institution with intelligent SMS workflows that secure transactions, reduce costs, and improve the overall customer banking experience.
        </p>

        {/* Responsive button (full width on small mobile, auto on desktop) */}
        <button
            onClick={handleSignupClick}
            className="inline-flex items-center justify-center gap-2 lg:gap-3 px-6 py-4 lg:px-10 lg:py-5 text-white bg-slate-900 rounded-xl transition duration-300 shadow-xl hover:shadow-2xl text-base lg:text-xl font-semibold w-full sm:w-auto"
        >
            Start Your Learning Journey
            <Target className="w-5 h-5 lg:w-6 lg:h-6" />
        </button>
    </div>
</section>
        </div>
    );
};

export default Bfsi;